import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import biogenFunctionOptions from 'basics/options/biogenFunction.options';
import useCepStatusOptions from 'basics/options/cepStatus.options';
import initiativeCountryOptions from 'basics/options/country.options';
import useProductOptions from 'basics/options/product.options';
import useTherapeuticAreaOptions from 'basics/options/therapeuticArea.options';
import { ProductType } from 'basics/types/products.types';
import { CurrentUser } from 'basics/types/user.types';
import useAdminDashboardFiltersSchema from 'components/AdminDashboardFilters/AdminDashboardFilters.schema';
import { AdminDashboardFiltersConfigType } from 'components/AdminDashboardFilters/AdminDashboardFilters.types';
import { ProductsContext } from 'contexts/products';
import { DateRange } from 'yoda-ui/components/Form/DateRangePicker';

const useAdminDashboardFiltersConfig = (
  therapeuticArea: string[] | null,
  commonCountryProducts: ProductType[],
  currentUserData: CurrentUser | null | undefined,
) => {
  const flags = useFlags();
  const { t } = useTranslation();
  const currentTA = therapeuticArea ?? [''];
  const adminDashboardFiltersSchema = useAdminDashboardFiltersSchema();
  const { allProducts } = useContext(ProductsContext);
  const products = flags.mayaAkma519EnableProductFilteringByCountry ? commonCountryProducts : allProducts;

  let dateStart = currentUserData?.adminDashboardFilters?.dateStart;
  if (typeof dateStart === 'string') {
    dateStart = new Date(dateStart);
    dateStart = new Date(
      dateStart.getUTCFullYear(),
      dateStart.getUTCMonth(),
      dateStart.getUTCDate(),
      dateStart.getUTCHours(),
      dateStart.getUTCMinutes(),
      dateStart.getUTCSeconds(),
    );
  }

  let dateEnd = currentUserData?.adminDashboardFilters?.dateEnd;
  if (typeof dateEnd === 'string') {
    dateEnd = new Date(dateEnd);
    dateEnd = new Date(
      dateEnd.getUTCFullYear(),
      dateEnd.getUTCMonth(),
      dateEnd.getUTCDate(),
      dateEnd.getUTCHours(),
      dateEnd.getUTCMinutes(),
      dateEnd.getUTCSeconds(),
    );
  }

  const dateRange: DateRange = [
    dateStart || null,
    dateEnd || null,
  ];

  const adminDashboardFiltersConfig: AdminDashboardFiltersConfigType = {
    countries: {
      name: 'countries',
      placeholder: t('maya_dashboard_filters_countries_placeholder'),
      required: true,
      defaultValue: currentUserData?.adminDashboardFilters?.countries || [],
      options: initiativeCountryOptions,
      validation: adminDashboardFiltersSchema.countries,
      enableSelectAll: true,
      disableRenderTags: true,
    },
    therapeuticArea: {
      name: 'therapeuticArea',
      placeholder: t('maya_dashboard_filters_ta_placeholder'),
      options: useTherapeuticAreaOptions(products),
      defaultValue: currentUserData?.adminDashboardFilters?.therapeuticArea || [],
      required: true,
      validation: adminDashboardFiltersSchema.therapeuticArea,
      enableSelectAll: true,
      disableRenderTags: true,
    },
    product: {
      name: 'product',
      placeholder: t('maya_dashboard_filters_product_placeholder'),
      options: useProductOptions(currentTA, products),
      defaultValue: currentUserData?.adminDashboardFilters?.product || [],
      required: false,
      validation: adminDashboardFiltersSchema.product,
      enableSelectAll: true,
      disableRenderTags: true,
    },
    cepFunction: {
      name: 'cepFunction',
      placeholder: t('maya_dashboard_filters_cep_function_placeholder'),
      options: biogenFunctionOptions,
      defaultValue: currentUserData?.adminDashboardFilters?.cepFunction || [],
      required: false,
      validation: adminDashboardFiltersSchema.cepFunction,
      enableSelectAll: true,
      disableRenderTags: true,
    },
    cepStatus: {
      name: 'cepStatus',
      placeholder: t('maya_dashboard_filters_cep_status_placeholder'),
      options: useCepStatusOptions(),
      defaultValue: currentUserData?.adminDashboardFilters?.cepStatus || [],
      required: false,
      validation: adminDashboardFiltersSchema.cepStatus,
      enableSelectAll: true,
      disableRenderTags: true,
    },
    date: {
      startText: '',
      endText: '',
      name: 'date',
      defaultValue: dateRange,
      disablePast: false,
      startRequired: false,
      endRequired: false,
      validation: adminDashboardFiltersSchema.date,
    },
  };

  return { adminDashboardFiltersConfig };
};

export default useAdminDashboardFiltersConfig;
